import React, { useContext, useMemo, useState } from 'react';
import {
    Table,
    TableBody,
    TableContainer,
    TablePagination,
    TableRow,
    TableCell,
    Typography,
    Avatar,
    Stack,
    Box,
    Chip,
    Tooltip,
} from '@mui/material';
import ProductsTableHead from './ProductsTableHead';
import { CommonContext } from '../../context/CommonContext';
import { ProductsContext } from '../../context/ProductsContext';
import moment from 'moment/moment';
import { Cancel, CheckCircle, EditNoteOutlined, Person, Inventory } from '@mui/icons-material';
import TableSkeleton from '../common/TableSkeleton';
import commonApi from '../../utils/api';
import { scrollToTop } from '../common/scrollToTop';

export default function ProductsTable() {
    const { firstLoading, allData, allProducts, selected, setSelected, page, setPage, rowsPerPage, setRowsPerPage, viewDetail, setIsLoading, onLoadApi } = useContext(ProductsContext);
    const { setOpenAlert, setAlertMessage, setSeverity, user } = useContext(CommonContext);

    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('created_at');

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(event.target.value);
        setPage(0);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = allProducts.map((item) => item.id);
            setSelected(newSelecteds);
        } else {
            setSelected([]);
        }
    };

    const isSelected = (id) => selected.indexOf(id) !== -1;

    const sortedRows = useMemo(() => {
        return [...allProducts].sort((a, b) => {
            if (order === 'asc') {
                return a[orderBy] > b[orderBy] ? 1 : -1;
            } else {
                return a[orderBy] < b[orderBy] ? 1 : -1;
            }
        });
    }, [allProducts, orderBy, order]);

    const toggleStatus = (id, status) => {
        setIsLoading(true);
        commonApi.post('/merchant-product/status', { id: id, status: status })
            .then((response) => {
                setIsLoading(false);
                setOpenAlert(true);
                scrollToTop();
                if (response?.data?.s === 1) {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('success');
                    onLoadApi();
                } else {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error')
                }
            })
            .catch((error) => {
                setIsLoading(false);
                setOpenAlert(true);
                setAlertMessage(error?.message || 'Something went wrong');
                setSeverity('error')
                console.error('Error fetching data:', error);
            });
    }
    const stickyActionStyle = {
        position: 'sticky',
        right: 0,
        backgroundColor: '#fff',
        borderLeft: '1px solid #ddd',
        boxShadow: 'inset 4px 0 8px -4px rgba(0, 0, 0, 0.2)', // subtle shadow on the left
        zIndex: 3,
    };
    return (
        <div className='table-component'>
            {firstLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1 }}>
                    <TablePagination
                        rowsPerPageOptions={[20, 50, 100, 150, 200, 250]}
                        component="div"
                        count={allData?.TotalRows || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Box>
            ) : null}
            <TableContainer>
                <Table size="small">
                    <ProductsTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        selected={selected}
                        onSelectAllClick={handleSelectAllClick}
                        rowCount={sortedRows.length}
                    />
                    <TableBody>
                        {!firstLoading ? (
                            <TableRow>
                                <TableCell colSpan={user?.user_type !== 'Merchant' ? 13 : 12} align="center" sx={{ p: 0 }}>
                                    <TableSkeleton rows={10} columns={user?.user_type !== 'Merchant' ? 13 : 12} />
                                </TableCell>
                            </TableRow>
                        ) : sortedRows.length > 0 ?
                            sortedRows.map((row, index) => {
                                const isItemSelected = isSelected(row.id);
                                const labelid = `enhanced-table-checkbox-${index}`;
                                return (
                                    <TableRow
                                        hover
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.id}
                                        selected={isItemSelected}
                                    >
                                        <TableCell
                                            component="th"
                                            id={labelid}
                                            scope="row"
                                        >
                                            {row.id}
                                        </TableCell>
                                        {user?.user_type !== 'Merchant' && (
                                            <TableCell align="left">
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#004d40' }}>
                                                        <Person sx={{ fontSize: 18, color: '#fff' }} />
                                                    </Avatar>
                                                    <Typography className='custome-typography'>
                                                        {row?.merchant?.company_name || row?.merchant?.brand_name || 'N/A'}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                        )}

                                        {/* Product Name */}
                                        <TableCell align="left">
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}>
                                                    {row?.name ? row.name.trim().charAt(0).toUpperCase() : <Inventory />}
                                                </Avatar>
                                                <Typography className='custome-typography'>
                                                    <Tooltip title={row.name} placement="top" arrow>
                                                        <div className='no-wrap'>{row.name}</div>
                                                    </Tooltip>
                                                </Typography>
                                            </Stack>
                                        </TableCell>

                                        {/* SKU Code */}
                                        <TableCell align="left">{row.sku_code || 'N/A'}</TableCell>

                                        {/* Serial No */}
                                        <TableCell align="left">{row.serial_no || 'N/A'}</TableCell>

                                        {/* Brand */}
                                        <TableCell align="left">
                                            <Typography className='custome-typography'>
                                                {row?.brand?.name || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        {/* Category */}
                                        <TableCell align="left">
                                            <Typography className='custome-typography'>
                                                {row?.category?.name || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        {/* Subcategory */}
                                        <TableCell align="left">
                                            <Typography className='custome-typography'>
                                                {row?.subcategory?.name || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        {/* Invoice Value */}
                                        <TableCell align="left">{row.invoice_value || 'N/A'}</TableCell>

                                        {/* GST Percentage */}
                                        <TableCell align="left">{row.gst_percentage || 'N/A'}</TableCell>

                                        <TableCell align="left">{row.created_at ? moment(row.created_at).format('DD-MM-YYYY') : 'NA'}</TableCell>
                                        <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                                            <Chip
                                                variant="outlined"
                                                label={row?.status === 1 ? 'Active' : 'InActive'}
                                                color={row?.status === 1 ? 'success' : 'error'}
                                                size="small"
                                                className="cutom-table-chip"
                                                icon={row?.status === 1 ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                                                onClick={() => toggleStatus(row.id, row?.status === 1 ? 0 : 1)}
                                            />
                                        </TableCell>
                                        <TableCell align="left"
                                            sx={stickyActionStyle}>
                                            <Chip
                                                variant="outlined"
                                                label={'Edit'}
                                                color={'default'}
                                                size="small"
                                                className="cutom-table-chip"
                                                icon={<EditNoteOutlined fontSize="small" />}
                                                onClick={() => viewDetail(row.id)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            }) : (
                                <TableRow>
                                    <TableCell colSpan={user?.user_type !== 'Merchant' ? 13 : 12} align="center">
                                        No data found
                                    </TableCell>
                                </TableRow>
                            )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
}
